import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MatchingQuestionnaire } from "@/components/matching/MatchingQuestionnaire";
import { MatchingOtpGate } from "@/components/matching/MatchingOtpGate";
import { MatchingResults } from "@/components/matching/MatchingResults";
import { CrisisResult } from "@/components/matching/CrisisResult";
import type { MatchingQuestionnaireAnswers, MatchingResponse } from "@/components/matching/types";
import { toast } from "@/lib/toast";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

const matchingMeta = definePageMeta({
  titleKey: "matching.meta.title",
  descriptionKey: "matching.meta.description",
  ogImage: "/og-find-therapist.jpg",
  structuredData: (ctx) => buildPageStructuredData(ctx, {
    titleKey: "matching.meta.title",
    descriptionKey: "matching.meta.description",
    ogImage: "/og-find-therapist.jpg",
    breadcrumbs: [
      { name: "Psihotron", path: "/" },
      { name: ctx.t("matching.meta.breadcrumb"), path: "/pronadji-terapeuta" },
    ],
  }),
});

export const Route = createFileRoute("/pronadji-terapeuta")(
  ssrPage({ component: MatchingPage, meta: matchingMeta }),
);

function getAnonymousSessionId(): string {
  if (typeof window === "undefined") return crypto.randomUUID();
  const key = "psihotron:matching-anonymous-session";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const next = crypto.randomUUID();
  window.localStorage.setItem(key, next);
  return next;
}

function MatchingPage() {
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const [draftAnswers, setDraftAnswers] = useState<MatchingQuestionnaireAnswers | null>(null);
  const [pendingAnswers, setPendingAnswers] = useState<MatchingQuestionnaireAnswers | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function isAlreadyAuthenticated(): Promise<boolean> {
    const res = await fetch("/api/auth/get-session", { credentials: "include" }).catch(() => null);
    if (!res?.ok) return false;
    const session = await res.json().catch(() => null);
    return Boolean((session as { user?: unknown } | null)?.user);
  }

  async function createRecommendations(answers: MatchingQuestionnaireAnswers) {
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/matching/recommendations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers,
          anonymousSessionId: getAnonymousSessionId(),
        }),
      });
      if (res.status === 429) {
        // Server vraća kod "rate_limited", ne rečenicu.
        throw new Error("Previše pokušaja u kratkom vremenu. Sačekajte malo pa pokušajte ponovo.");
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || "Greška pri izradi preporuka.");
      }
      setPendingAnswers(null);
      setResult(await res.json() as MatchingResponse);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Greška pri izradi preporuka.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submit(answers: MatchingQuestionnaireAnswers) {
    setDraftAnswers(answers);
    if (answers.selfHarm === "active_plan" || await isAlreadyAuthenticated()) {
      await createRecommendations(answers);
      return;
    }
    setPendingAnswers(answers);
  }

  function restart() {
    setResult(null);
    setPendingAnswers(null);
    setDraftAnswers(null);
  }

  if (result?.riskLevel === "crisis" && result.crisis) {
    return <CrisisResult crisis={result.crisis} onRestart={restart} />;
  }

  if (result) {
    return <MatchingResults result={result} onRestart={restart} />;
  }

  if (pendingAnswers) {
    return (
      <MatchingOtpGate
        busy={isSubmitting}
        onBack={() => setPendingAnswers(null)}
        onAuthenticated={() => createRecommendations(pendingAnswers)}
      />
    );
  }

  return (
    <MatchingQuestionnaire
      onSubmit={submit}
      isSubmitting={isSubmitting}
      initialAnswers={draftAnswers ?? undefined}
      initialStarted={Boolean(draftAnswers)}
      resumeAtEnd={Boolean(draftAnswers)}
    />
  );
}
